import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type { PageResponse } from '@/lib/api/types'
import type {
  HelpdeskSummaryView, KbArticleView, TicketCategoryView, TicketCommentView,
  TicketEscalationView, TicketSatisfactionView, TicketView,
} from '../types'

const BASE = '/v1/helpdesk'
const KB = '/v1/kb'

export interface TicketListParams {
  page?: number
  size?: number
  sort?: string
  q?: string
  categoryId?: string
  status?: string
  assigneeUserId?: string
}

export function useTickets(params: TicketListParams) {
  return useQuery({
    queryKey: ['helpdesk', 'tickets', params],
    queryFn: async () =>
      (await apiClient.get<PageResponse<TicketView>>(`${BASE}/tickets`, {
        params: {
          page: params.page,
          size: params.size,
          sort: params.sort,
          q: params.q || undefined,
          categoryId: params.categoryId || undefined,
          status: params.status || undefined,
          assigneeUserId: params.assigneeUserId || undefined,
        },
      })).data,
    placeholderData: keepPreviousData,
  })
}

export function useTicket(id: string | undefined) {
  return useQuery({
    queryKey: ['helpdesk', 'ticket', id],
    enabled: Boolean(id),
    queryFn: async () => (await apiClient.get<TicketView>(`${BASE}/tickets/${id}`)).data,
  })
}

export function useTicketComments(id: string | undefined) {
  return useQuery({
    queryKey: ['helpdesk', 'ticket', id, 'comments'],
    enabled: Boolean(id),
    queryFn: async () =>
      (await apiClient.get<TicketCommentView[]>(`${BASE}/tickets/${id}/comments`)).data,
  })
}

export function useTicketEscalations(id: string | undefined) {
  return useQuery({
    queryKey: ['helpdesk', 'ticket', id, 'escalations'],
    enabled: Boolean(id),
    queryFn: async () =>
      (await apiClient.get<TicketEscalationView[]>(`${BASE}/tickets/${id}/escalations`)).data,
  })
}

export function useTicketCategories() {
  return useQuery({
    queryKey: ['helpdesk', 'categories'],
    queryFn: async () => (await apiClient.get<TicketCategoryView[]>(`${BASE}/categories`)).data,
  })
}

export function useHelpdeskSummary() {
  return useQuery({
    queryKey: ['helpdesk', 'summary'],
    queryFn: async () => (await apiClient.get<HelpdeskSummaryView>(`${BASE}/summary`)).data,
  })
}

export function useKbArticles(params: { q?: string; status?: string; page?: number }) {
  return useQuery({
    queryKey: ['helpdesk', 'kb', params],
    queryFn: async () =>
      (await apiClient.get<PageResponse<KbArticleView>>(KB, {
        params: { q: params.q || undefined, status: params.status || undefined, page: params.page },
      })).data,
    placeholderData: keepPreviousData,
  })
}

export function useHelpdeskMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['helpdesk'] })

  return {
    saveCategory: useMutation({
      mutationFn: async ({ id, body }: { id?: string; body: Record<string, unknown> }) =>
        id
          ? (await apiClient.put<TicketCategoryView>(`${BASE}/categories/${id}`, body)).data
          : (await apiClient.post<TicketCategoryView>(`${BASE}/categories`, body)).data,
      onSuccess: invalidate,
    }),
    /** Retires a category. A closed ticket names the category it was filed under. */
    retireCategory: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<TicketCategoryView>(`${BASE}/categories/${id}/deactivate`)).data,
      onSuccess: invalidate,
    }),
    raise: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<TicketView>(`${BASE}/tickets`, body)).data,
      onSuccess: invalidate,
    }),
    assign: useMutation({
      mutationFn: async ({ id, assigneeUserId }: { id: string; assigneeUserId: string | null }) =>
        (await apiClient.post<TicketView>(`${BASE}/tickets/${id}/assign`, { assigneeUserId })).data,
      onSuccess: invalidate,
    }),
    comment: useMutation({
      mutationFn: async ({ id, body, internal }: {
        id: string; body: string; internal: boolean
      }) => (await apiClient.post<TicketCommentView>(
        `${BASE}/tickets/${id}/comments`, { body, internal })).data,
      onSuccess: invalidate,
    }),
    resolve: useMutation({
      mutationFn: async ({ id, resolution }: { id: string; resolution: string }) =>
        (await apiClient.post<TicketView>(`${BASE}/tickets/${id}/resolve`, { resolution })).data,
      onSuccess: invalidate,
    }),
    reopen: useMutation({
      mutationFn: async ({ id, reason }: { id: string; reason: string }) =>
        (await apiClient.post<TicketView>(`${BASE}/tickets/${id}/reopen`, { reason })).data,
      onSuccess: invalidate,
    }),
    close: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<TicketView>(`${BASE}/tickets/${id}/close`)).data,
      onSuccess: invalidate,
    }),
    rate: useMutation({
      mutationFn: async ({ id, score, comment }: {
        id: string; score: number; comment: string | null
      }) => (await apiClient.post<TicketSatisfactionView>(
        `${BASE}/tickets/${id}/rate`, { score, comment })).data,
      onSuccess: invalidate,
    }),
    saveArticle: useMutation({
      mutationFn: async ({ id, body }: { id?: string; body: Record<string, unknown> }) =>
        id
          ? (await apiClient.put<KbArticleView>(`${KB}/${id}`, body)).data
          : (await apiClient.post<KbArticleView>(KB, body)).data,
      onSuccess: invalidate,
    }),
    /** Archives an article: it stops being searchable and keeps its history. */
    archiveArticle: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<KbArticleView>(`${KB}/${id}/deactivate`)).data,
      onSuccess: invalidate,
    }),
    publishArticle: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<KbArticleView>(`${KB}/${id}/publish`)).data,
      onSuccess: invalidate,
    }),
    rateArticle: useMutation({
      mutationFn: async ({ id, helpful }: { id: string; helpful: boolean }) =>
        (await apiClient.post<KbArticleView>(`${KB}/${id}/helpful?helpful=${helpful}`)).data,
      onSuccess: invalidate,
    }),
  }
}
